import { useEffect, useEffectEvent, useRef, type InputHTMLAttributes } from 'react';
import { cx } from '@/core/utils';
import { FIELD } from './fieldStyles';

type Props = Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'value' | 'defaultValue' | 'onChange'> & {
  value: string;
  /** Called with the raw text when the user commits an edit (blur, Enter, spinner), not on every keystroke. */
  onCommit: (value: string) => void;
};

/** Number input that reports committed values only, like the native `change` event. */
export function NumberInput({ value, onCommit, className, ...props }: Props) {
  const ref = useRef<HTMLInputElement>(null);
  const commit = useEffectEvent((v: string) => onCommit(v));

  useEffect(() => {
    const el = ref.current!;
    const onChange = () => commit(el.value);
    el.addEventListener('change', onChange);
    return () => el.removeEventListener('change', onChange);
  }, []);

  // Uncontrolled while typing; external changes (e.g. unit conversion) overwrite the text.
  useEffect(() => {
    if (ref.current && ref.current.value !== value) ref.current.value = value;
  }, [value]);

  return (
    <input
      ref={ref}
      type="number"
      inputMode="decimal"
      enterKeyHint="done"
      defaultValue={value}
      className={cx(FIELD, 'text-right', className)}
      {...props}
    />
  );
}
